import { Button } from '@pms-digital/design-system';
import { EXPORT_FORMAT_LABELS, type ExportFormat, exportButtonLabel } from './export-selection';

const FORMAT_OPTIONS: ExportFormat[] = ['excel', 'pdf', 'html'];

interface ExportFooterProps {
  format: ExportFormat;
  onFormatChange: (format: ExportFormat) => void;
  onExport: () => void;
  isExporting: boolean;
  hasFailed: boolean;
  /** The company is archived: the backend refuses every export of its matrices (issue #22). */
  archived?: boolean;
}

/**
 * The export page's permanent action bar. A `shrink-0` flex sibling pinned
 * below the scrolling option panel rather than a `position: fixed` element —
 * `__root` caps the app at `h-screen`, so the sidebar column's bottom edge is
 * already the bottom of the usable viewport on both breakpoints. Fixed
 * positioning would have needed a hardcoded left offset coupled to the rail's
 * width.
 */
export function ExportFooter({
  format,
  onFormatChange,
  onExport,
  isExporting,
  hasFailed,
  archived = false,
}: ExportFooterProps) {
  return (
    <div className="shrink-0 space-y-2 border-gray-200 border-t pt-3">
      {hasFailed && (
        <p role="alert" className="text-danger text-small">
          L'export a échoué, réessayez.
        </p>
      )}
      <div className="flex gap-2">
        {FORMAT_OPTIONS.map((option) => (
          <div key={option} className="min-w-0 flex-1">
            <Button
              type="button"
              size="sm"
              fullWidth
              variant={format === option ? 'secondary' : 'primary'}
              aria-pressed={format === option}
              onClick={() => onFormatChange(option)}
            >
              {EXPORT_FORMAT_LABELS[option]}
            </Button>
          </div>
        ))}
      </div>
      {archived && (
        <p className="text-gray-600 text-small">
          Entreprise archivée : l'export est désactivé tant qu'elle n'est pas restaurée.
        </p>
      )}
      <Button
        type="button"
        variant="secondary"
        fullWidth
        disabled={isExporting || archived}
        loading={isExporting}
        loadingLabel="Export en cours…"
        onClick={onExport}
      >
        {exportButtonLabel(format)}
      </Button>
    </div>
  );
}
